import { parseJwtPayload } from './jwtClaims';

const ACCESS_KEY = 'jwt.accessToken';
const REFRESH_KEY = 'jwt.refreshToken';
const EXPIRES_KEY = 'jwt.expiresAt';

export const TOKEN_STORAGE_KEYS = [ACCESS_KEY, REFRESH_KEY, EXPIRES_KEY];

function getSessionStorage() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }
  return window.sessionStorage;
}

function getLocalStorage() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  return window.localStorage;
}

function readItem(key) {
  const session = getSessionStorage();
  const local = getLocalStorage();
  const fromLocal = local?.getItem(key);

  if (fromLocal !== null && fromLocal !== '') {
    if (session?.getItem(key) !== fromLocal) {
      session?.setItem(key, fromLocal);
    }
    return fromLocal;
  }

  session?.removeItem(key);
  return null;
}

function writeItem(key, value) {
  getSessionStorage()?.setItem(key, value);
  getLocalStorage()?.setItem(key, value);
}

function removeItem(key) {
  getSessionStorage()?.removeItem(key);
  getLocalStorage()?.removeItem(key);
}

export function hydrateTabFromSharedStorage() {
  TOKEN_STORAGE_KEYS.forEach((key) => {
    readItem(key);
  });
}

export function setTokens({ accessToken, refreshToken, expiresIn }) {
  if (!getSessionStorage() || !accessToken) {
    return;
  }

  writeItem(ACCESS_KEY, accessToken);
  if (refreshToken) {
    writeItem(REFRESH_KEY, refreshToken);
  }

  if (expiresIn) {
    writeItem(EXPIRES_KEY, String(Date.now() + expiresIn * 1000));
    return;
  }

  const payload = parseJwtPayload(accessToken);
  if (payload?.exp) {
    writeItem(EXPIRES_KEY, String(payload.exp * 1000));
  }
}

export function applyTokensFromSync({ accessToken, refreshToken, expiresAt }) {
  if (!getSessionStorage() || !accessToken) {
    return;
  }

  writeItem(ACCESS_KEY, accessToken);
  if (refreshToken) {
    writeItem(REFRESH_KEY, refreshToken);
  }
  if (expiresAt) {
    writeItem(EXPIRES_KEY, String(expiresAt));
  }
}

export function getAccessToken() {
  return readItem(ACCESS_KEY);
}

export function getRefreshToken() {
  return readItem(REFRESH_KEY);
}

export function getExpiresAt() {
  const raw = readItem(EXPIRES_KEY);
  return raw ? Number(raw) : null;
}

export function isExpired(skewMs = 5000) {
  const expiresAt = getExpiresAt();
  if (expiresAt) {
    return Date.now() >= expiresAt - skewMs;
  }

  const accessToken = getAccessToken();
  if (accessToken) {
    const payload = parseJwtPayload(accessToken);
    if (payload?.exp) {
      return Date.now() >= payload.exp * 1000 - skewMs;
    }
  }

  return true;
}

export function clearTokens() {
  TOKEN_STORAGE_KEYS.forEach(removeItem);
}

export function clearTabTokensOnly() {
  TOKEN_STORAGE_KEYS.forEach((key) => {
    getSessionStorage()?.removeItem(key);
  });
}

export function restore() {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return null;
  }

  return {
    accessToken,
    refreshToken: getRefreshToken(),
    expiresAt: getExpiresAt()
  };
}
